import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, isSignInWithEmailLink, signInWithEmailLink, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, getDoc, updateDoc, getFirestore } from 'firebase/firestore';
import logoSvg from '../assets/logo.svg';

/**
 * Componente para processar convites enviados usando o Firebase Auth
 * Esta página é acessada quando o usuário clica no link de email enviado
 */
const AcceptInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('inviteId');
  const isReminder = searchParams.get('reminder') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('loading'); // loading, email, form, success, error
  const [email, setEmail] = useState('');
  const [invitation, setInvitation] = useState(null);
  
  const [formData, setFormData] = useState({
    displayName: '',
    password: '',
    confirmPassword: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Verificar se esta é uma URL de convite válida
  useEffect(() => {
    const verifyEmailLink = async () => {
      const auth = getAuth();
      
      // Verificar se a URL atual é um link de login por email válido
      if (isSignInWithEmailLink(auth, window.location.href)) {
        try {
          setLoading(true);
          
          // Se temos um ID de convite, buscar detalhes
          if (inviteId) {
            const db = getFirestore();
            const inviteRef = doc(db, 'email_invitations', inviteId);
            const inviteDoc = await getDoc(inviteRef);
            
            if (inviteDoc.exists()) {
              const inviteData = inviteDoc.data();
              setInvitation(inviteData);
              
              // Preencher o email se temos no convite
              if (inviteData.email) {
                setEmail(inviteData.email);
                setStep('form');
                return;
              }
            } else {
              throw new Error('Convite não encontrado');
            }
          }
          
          // Se não temos email, pedir ao usuário
          setStep('email');
        } catch (error) {
          console.error('Erro ao verificar convite:', error);
          setError('Não foi possível verificar o convite. Tente novamente mais tarde.');
          setStep('error');
        } finally {
          setLoading(false);
        }
      } else {
        setError('Esta URL não é válida para aceitar um convite.');
        setStep('error');
        setLoading(false);
      }
    };
    
    verifyEmailLink();
  }, [inviteId]);
  
  // Processar submissão do email
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setFormErrors({ email: 'O email é obrigatório' });
      return;
    }
    
    setStep('form');
  };
  
  // Processar mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      setEmail(value);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Limpar erro do campo
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Validar formulário antes de submeter
  const validateForm = () => {
    const errors = {};
    
    if (!formData.displayName.trim()) {
      errors.displayName = 'Nome é obrigatório';
    }
    
    if (!formData.password) {
      errors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      errors.password = 'A senha deve ter pelo menos 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Submeter formulário de aceitação
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const auth = getAuth();
      
      // Tentar fazer login com o link do email
      try {
        // Criar uma nova conta ou fazer login
        await signInWithEmailLink(auth, email, window.location.href);
        
        // Atualizar perfil do usuário
        if (auth.currentUser) {
          await updateProfile(auth.currentUser, {
            displayName: formData.displayName
          });
        }
      } catch (emailLinkError) {
        console.error('Erro ao fazer login com link de email:', emailLinkError);
        
        // Se o login falhar, criar uma nova conta
        const userCredential = await createUserWithEmailAndPassword(auth, email, formData.password);
        
        // Atualizar perfil do usuário
        await updateProfile(userCredential.user, {
          displayName: formData.displayName
        });
      }
      
      // Atualizar o status do convite se temos um ID
      if (inviteId) {
        const db = getFirestore();
        const inviteRef = doc(db, 'email_invitations', inviteId);
        
        await updateDoc(inviteRef, {
          status: 'accepted',
          acceptedAt: new Date()
        });
      }
      
      // Mostrar tela de sucesso
      setStep('success');
      
      // Redirecionar após 3 segundos
      setTimeout(() => {
        // Redirecionar com base no papel (role) do usuário
        const role = invitation?.role || 'professional';
        const salonId = invitation?.salonId;
        
        if (role === 'professional') {
          navigate('/professional');
        } else if (salonId) {
          navigate(`/${salonId}/admin`);
        } else {
          navigate('/');
        }
      }, 3000);
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      setError(error.message || 'Ocorreu um erro ao aceitar o convite. Tente novamente mais tarde.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Renderizar etapa atual
  const renderStep = () => {
    switch (step) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Verificando convite...</p>
          </div>
        );
        
      case 'email':
        return (
          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Confirme seu Email</h2>
            <p className="text-gray-600 mb-6">
              Para completar o processo, por favor confirme o email para o qual o convite foi enviado.
            </p>
            
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.email ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continuar
              </button>
            </form>
          </div>
        );
        
      case 'form':
        return (
          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Aceitar Convite</h2>
            <p className="text-gray-600 mb-6">
              {invitation && (
                <>
                  Você foi convidado por {invitation.senderName || 'um administrador'} para se juntar a {' '}
                  {invitation.salonName || 'um salão'} como {' '}
                  {invitation.role === 'professional' ? 'profissional' : 'gestor'}.
                </>
              )}
              {!invitation && 'Complete seu cadastro para aceitar o convite.'}
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    readOnly
                    className="bg-gray-100 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Este é o email associado ao seu convite</p>
                </div>
              </div>
              
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                  Nome Completo
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.displayName ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.displayName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.displayName}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.password ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirmar Senha
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.confirmPassword ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
              
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {submitting ? 'Processando...' : 'Aceitar Convite'}
              </button>
            </form>
          </div>
        );
      
      case 'success':
        return (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Convite Aceito!</h2>
            <p className="text-gray-600 mb-4">
              Você agora faz parte da equipe{invitation?.salonName ? ` de ${invitation.salonName}` : ''}. 
              Você será redirecionado em breve.
            </p>
            <div className="animate-pulse text-blue-500">
              Redirecionando...
            </div>
          </div>
        );
        
      case 'error':
        return (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Erro ao Processar Convite</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Voltar à Página Inicial
            </button>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center">
            <img src={logoSvg} alt="Bookly" className="h-14" />
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
          {renderStep()}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-white py-4 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Bookly · Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
};

export default AcceptInvitePage; 